import { useCallback, useEffect, useState } from 'react';
import { get80DnaQuestion, post81DnaSave } from '../api/dnaService';

/**
 * 將 API 80 回傳整理為題目列表與 record_id（與 mock / 真實回應格式相容）
 */
export function normalizeQuestionPayload(raw) {
  const body = raw?.data ?? raw ?? {};
  const questions = Array.isArray(body.questions) ? body.questions : [];
  const recordId = body.record_id != null ? Number(body.record_id) : null;
  return {
    message: body.message,
    resultCode: body.result_code,
    currentStatus: body.current_status,
    progress: body.progress ?? {},
    questions,
    recordId,
  };
}

/**
 * DNA 測驗作答流程：載入題目、累積答案、每次選項後呼叫 81 儲存。
 */
export function useDnaQuestionFlow() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await get80DnaQuestion();
      const normalized = normalizeQuestionPayload(res);
      if (normalized.recordId == null) {
        setError(new Error('缺少作答紀錄 ID'));
        setQuestions([]);
        setRecordId(null);
        return;
      }
      setQuestions(normalized.questions);
      setRecordId(normalized.recordId);
      setCurrentIndex(0);
      setAnswers({});
    } catch (e) {
      console.error(e);
      setError(e);
      setQuestions([]);
      setRecordId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const currentQuestion = questions[currentIndex] ?? null;
  const total = questions.length;

  /**
   * 選擇選項：合併 response、呼叫 post81，最後一題時回傳 true 表示可導向結果頁。
   */
  const chooseOption = useCallback(
    async (optionId) => {
      const q = questions[currentIndex];
      if (!q || recordId == null) return { done: false };

      const nextAnswers = {
        ...answers,
        [q.question_id]: optionId,
      };

      setSaving(true);
      setError(null);
      try {
        await post81DnaSave(recordId, nextAnswers);
        setAnswers(nextAnswers);

        if (currentIndex >= questions.length - 1) {
          return { done: true, recordId };
        }

        setCurrentIndex((i) => i + 1);
        return { done: false, recordId };
      } catch (e) {
        console.error(e);
        setError(e);
        return { done: false };
      } finally {
        setSaving(false);
      }
    },
    [answers, currentIndex, questions, recordId],
  );

  return {
    loading,
    error,
    questions,
    recordId,
    currentIndex,
    total,
    currentQuestion,
    saving,
    answers,
    reload: load,
    chooseOption,
  };
}
